import { h as __h } from "preact";
import { signal } from "@preact/signals";
/** @import { Signal } from "@preact/signals"; */
import { Component, createRef } from "preact";
/** @import { RefObject } from "preact"; */

import { passkeyResponse, stepUpTokenResponse } from "~/backend/validate.js";
import { authedApiRequest, query, response } from "~/lib/api.js";
/** @import { ApiError } from "~/lib/api.js"; */
import { identity } from "~/lib/identity.js";
import { stepUp as passkeyStepUp } from "~/lib/passkey.js";
/** @import { StepUpAction } from "~/lib/passkey.js"; */

import html from "~/util/html.js";
import { array } from "~/util/validate.js";
import Dropdown from "./Dropdown.js";
import Field from "./Field.js";
import Icon from "./Icon.js";
import button from "~/style/button.js";
import input from "~/style/input.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./StepUp.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./StepUp.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @extends {Component<StepUpProps>}
 *
 * @typedef {object} StepUpProps
 * @property {ApiError} [error]
 * @property {RefObject<StepUp>} [ref]
 */
export default class StepUp extends Component {
  /**
   * @param {StepUpAction} action
   * @returns {Promise<string>}
   */
  async requestToken(action) {
    switch (this.#choice) {
      case "password":
        {
          const password = /** @type {string} */this.#inputPasswordRef.current?.value;
          const {
            data
          } = await authedApiRequest("POST", "/v2/auth/step-up", {
            json: {
              action,
              password,
              tfa_code: this.#inputTfaRef.current?.value
            },
            schema: response(stepUpTokenResponse)
          });
          return data.token;
        }
      case "passkey":
        {
          return passkeyStepUp(action);
        }
    }
  }
  #id = crypto.randomUUID();

  /** @type {Signal<"default" | "password" | "passkey">} */
  #choiceRaw = signal("default");
  get #choice() {
    const choice = this.#choiceRaw.value;
    if (choice === "default") {
      return this.#hasPasskey ? "passkey" : "password";
    }
    return choice;
  }

  /** @param {"password" | "passkey"} choice */
  #choiceText(choice) {
    switch (choice) {
      case "password":
        {
          return identity.me?.has_tfa ? "Password + 2FA" : "Password";
        }
      case "passkey":
        {
          return "Passkey";
        }
    }
  }

  /** @type {RefObject<HTMLInputElement>} */
  #inputPasswordRef = createRef();
  /** @type {RefObject<HTMLInputElement>} */
  #inputTfaRef = createRef();
  #passkeys = query(() => ({
    key: ["/v2/passkeys"]
  }), {
    schema: response(array(passkeyResponse))
  });
  get #hasPasskey() {
    return this.#passkeys.data?.data.length;
  }
  render() {
    const choice = this.#choice;
    return __h("div", {
      "class": css.main
    }, __h("div", {
      "class": css.selector
    }, "Verify your account with", __h(Dropdown, {
      id: "stepup-dropdown-" + this.#id,
      placement: "bottom-start",
      items: [{
        text: this.#choiceText("password"),
        onSelect: () => {
          this.#choiceRaw.value = "password";
        }
      }, {
        text: this.#choiceText("passkey"),
        ...(this.#hasPasskey ? {} : {
          disabled: true,
          tooltip: "You don't have any passkeys"
        }),
        onSelect: () => {
          this.#choiceRaw.value = "passkey";
        }
      }],
      trigger: __h("button", {
        "class": button.button + " " + css.button,
        type: "button",
        "data-level": "tertiary",
        popovertarget: "stepup-dropdown-" + this.#id
      }, this.#choiceText(choice), __h("div", {
        "class": css.arrow
      }, __h(Icon, {
        name: "down-filled",
        size: 14
      })))
    })), choice === "password" && [__h(Field, {
      "for": "stepup-password-" + this.#id,
      label: "Current password",
      error: this.props.error?.fields.password?.message
    }, __h("input", {
      ref: this.#inputPasswordRef,
      "class": input.input,
      id: "stepup-password-" + this.#id,
      type: "password",
      name: "password",
      required: true
    })), identity.me?.has_tfa && __h(Field, {
      "for": "stepup-tfa_code-" + this.#id,
      label: "Authenticator code",
      error: this.props.error?.fields.tfa_code?.message
    }, __h("input", {
      ref: this.#inputTfaRef,
      "class": input.input,
      id: "stepup-tfa_code-" + this.#id,
      type: "text",
      name: "tfa_code",
      required: true,
      inputmode: "numeric",
      autocomplete: "one-time-code",
      pattern: "[0-9]{6}",
      maxlength: 6
    }))], choice === "passkey" && __h("div", {
      "class": css.passkeyCallout
    }, "You will be prompted to use your passkey."));
  }
}